// Header control (white on brand-dark): who this device is acting as, and a menu to
// switch persona, see the sandbox code, open the demo page or leave. The menu is
// portalled to <body> so it is not trapped in the sticky header's stacking context
// (the bottom navigation would otherwise cover it) and does not inherit white text.
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Badge, Banner, Button, cx, Sheet, Spinner } from '@/components/ui'
import { useMe, useSession } from '@/lib/queries'
import { LinkButton } from '@/features/home/LinkButton'
import { CopyButton, DemoError } from './parts'
import {
  catalogueEntry,
  DEMO_PERSONA_CATALOGUE,
  formatSandboxCode,
  isNegativeCase,
  personaRoleSummary,
  personaUse,
  type PersonaUse,
  rolesLabel,
  shortName,
  uniqueRoleCodes,
} from './personas'
import { useAsyncAction, useDemoActions, useDemoStatus } from './useDemo'

export function PersonaSwitcher() {
  const session = useSession()
  const me = useMe()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)

  if (!session.activePersona) {
    if (location.pathname === '/demo') return null
    return (
      <Link
        to="/demo"
        className="tap inline-flex items-center rounded-lg border border-white/40 px-3 text-sm font-semibold text-white hover:bg-white/10"
      >
        Start demo
      </Link>
    )
  }

  const entry = catalogueEntry(session.activePersona)
  const name = me.data?.person.display_name ?? entry?.name ?? session.activePersona
  const codes = me.data ? uniqueRoleCodes(me.data.credentials.map((c) => c.role_code)) : []
  const roleLine = codes.length > 0 ? codes.join(' + ') : (entry?.role ?? session.activePersona)

  const close = () => {
    setOpen(false)
    window.setTimeout(() => trigger.current?.focus(), 0)
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex max-w-[9.5rem] items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-2.5 py-1 text-left text-white hover:bg-white/20 sm:max-w-[16rem]"
      >
        <span className="min-w-0 flex-1">
          <span className="sr-only">Demo persona: </span>
          {/* The name wraps instead of being cut off: who is signing must always be readable. The role line
              stays one line (the persona menu shows it in full) so the sticky header stays short on a phone. */}
          <span className="block break-words text-sm font-semibold leading-tight">{shortName(name)}</span>
          <span className="block truncate text-xs leading-tight text-white/80" title={roleLine}>
            {roleLine}
          </span>
        </span>
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? createPortal(<PersonaMenu onClose={close} />, document.body) : null}
    </>
  )
}

type Row = { code: string; name: string; summary: string; use: PersonaUse | 'unknown'; negative: boolean }

const USE_LABEL: Record<Row['use'], string> = {
  this_device: 'Signed in here',
  elsewhere: 'In use elsewhere',
  unused: '',
  unknown: '',
}

function PersonaMenu({ onClose }: { onClose: () => void }) {
  const session = useSession()
  const me = useMe()
  const status = useDemoStatus()
  const actions = useDemoActions()
  const navigate = useNavigate()
  const { busy, error, run } = useAsyncAction()
  const [confirm, setConfirm] = useState<string | null>(null)
  const [target, setTarget] = useState<string | null>(null)
  const [leaving, setLeaving] = useState(false)
  const body = useRef<HTMLDivElement>(null)
  const { refetch } = status

  // Fresh binding state each time the menu opens; Escape closes; focus moves into the menu.
  useEffect(() => {
    void refetch()
  }, [refetch])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  useEffect(() => {
    body.current?.closest<HTMLElement>('[role="dialog"]')?.querySelector<HTMLElement>('button')?.focus()
  }, [])

  const rows: Row[] = status.data
    ? status.data.personas.map((p) => ({
        code: p.persona_code,
        name: p.display_name,
        summary: personaRoleSummary(p),
        use: personaUse(p, session.boundPersonas),
        negative: isNegativeCase(p),
      }))
    : DEMO_PERSONA_CATALOGUE.map((c) => ({
        code: c.code,
        name: c.name,
        summary: c.role,
        use: session.boundPersonas.includes(c.code) ? 'this_device' : 'unknown',
        negative: c.negative,
      }))

  const choose = async (row: Row) => {
    if (row.use === 'elsewhere' && confirm !== row.code) {
      setConfirm(row.code)
      return
    }
    setConfirm(null)
    setTarget(shortName(row.name))
    const ok = await run(row.code, () => actions.switchTo(row.code))
    if (ok) onClose()
  }

  const leave = async () => {
    const ok = await run('leave', actions.leave)
    if (ok) {
      onClose()
      navigate('/demo', { replace: true })
    }
  }

  const renderRow = (row: Row) => {
    const isActive = row.code === session.activePersona
    return (
      <li key={row.code}>
        {isActive ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-brand bg-brand-light px-3 py-2">
            <RowText row={row} />
            <Badge tone="ok">Current</Badge>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void choose(row)}
            disabled={busy !== null}
            className={cx(
              'flex w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-left hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-60',
              confirm === row.code ? 'border-warn' : 'border-line',
            )}
          >
            <RowText row={row} />
            <span className="shrink-0 text-xs font-semibold text-ink-soft">{busy === row.code ? <Spinner small /> : USE_LABEL[row.use]}</span>
          </button>
        )}
        {confirm === row.code ? (
          <div className="mt-1 space-y-2 rounded-lg border border-warn/30 bg-warn-light p-2">
            <p className="text-sm text-ink">
              {shortName(row.name)} is in use in another browser or device. Acting as them here moves the persona to this device,
              and the other device loses it.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void choose(row)} loading={busy === row.code} disabled={busy !== null}>
                Move here and act
              </Button>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </li>
    )
  }

  const lifecycle = rows.filter((r) => !r.negative)
  const negative = rows.filter((r) => r.negative)
  const myRoles = me.data ? rolesLabel(me.data.credentials.map((c) => c.role_code)) : ''

  return (
    <Sheet open title="Demo persona" onClose={onClose}>
      <div ref={body} className="space-y-4 text-ink">
        <div className="rounded-lg border border-line bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Acting as</p>
          <p className="font-semibold">{me.data?.person.display_name ?? catalogueEntry(session.activePersona)?.name ?? session.activePersona}</p>
          {me.data?.person.organisation ? <p className="text-sm text-ink-soft">{me.data.person.organisation}</p> : null}
          <p className="text-sm text-ink-soft">{myRoles || catalogueEntry(session.activePersona)?.role}</p>
          <p className="mt-1 text-xs text-ink-soft">Synthetic persona. Each persona is its own sign-in; the server records who signs and when.</p>
        </div>

        <section aria-labelledby="switch-heading">
          <h3 id="switch-heading" className="display mb-2 text-base font-semibold">
            Switch persona
          </h3>
          <DemoError error={error} title={target ? `Could not switch to ${target}` : 'Could not switch persona'} />
          {status.isLoading ? (
            <div className="flex justify-center p-3">
              <Spinner />
            </div>
          ) : (
            <>
              {status.error ? (
                <Banner tone="warn" className="mb-2">
                  Could not check which personas are in use on other devices. Acting as one of them may move it from another device.
                </Banner>
              ) : null}
              <ul className="space-y-2">{lifecycle.map(renderRow)}</ul>
              {negative.length > 0 ? (
                <details className="mt-3 rounded-lg border border-dashed border-line-strong p-2">
                  <summary className="tap flex cursor-pointer items-center text-sm font-semibold">Negative test cases ({negative.length})</summary>
                  <p className="mb-2 text-xs text-ink-soft">Personas that show the server refusing: expired, revoked or out-of-scope licences, or another site.</p>
                  <ul className="space-y-2">{negative.map(renderRow)}</ul>
                </details>
              ) : null}
            </>
          )}
        </section>

        {session.sandboxCode ? (
          <div className="rounded-lg border border-line bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Sandbox code</p>
            <p className="code select-all text-2xl font-bold tracking-[0.15em]">{formatSandboxCode(session.sandboxCode)}</p>
            <p className="mb-2 text-xs text-ink-soft">Another phone or laptop can join this demo with this code.</p>
            <CopyButton text={session.sandboxCode} label="Copy code" />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <LinkButton to="/demo" variant="secondary" onClick={onClose}>
            Demo and devices
          </LinkButton>
          <LinkButton to="/about" variant="ghost" onClick={onClose}>
            About this demo
          </LinkButton>
        </div>

        <div className="border-t border-line pt-3">
          {leaving ? (
            <div className="space-y-2">
              <p className="text-sm">Sign every persona on this device out of the demo? You can join again with the sandbox code.</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="danger" onClick={() => void leave()} loading={busy === 'leave'} disabled={busy !== null && busy !== 'leave'}>
                  Yes, leave the demo
                </Button>
                <Button variant="secondary" onClick={() => setLeaving(false)}>
                  Stay
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setLeaving(true)}>
              Leave demo
            </Button>
          )}
        </div>
      </div>
    </Sheet>
  )
}

function RowText({ row }: { row: Row }) {
  return (
    <span className="min-w-0">
      <span className="block font-semibold text-ink">
        {shortName(row.name)} <span className="code text-xs font-normal text-ink-faint">{row.code}</span>
      </span>
      <span className="block text-xs text-ink-soft">{row.summary}</span>
    </span>
  )
}
